import type { CareRequest } from '../hooks/useCareRequests';
import StatusDropdown from './StatusDropdown';

interface Props {
  rows: CareRequest[];
  onStatusChange: (id: number, status: CareRequest['status']) => Promise<boolean>;
  onRowClick: (row: CareRequest) => void;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${m}.${day} ${h}:${min}`;
}

export default function CareRequestTable({ rows, onStatusChange, onRowClick }: Props) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-100 mb-4">
          <i className="ri-file-list-3-line text-2xl text-gray-300"></i>
        </div>
        <p className="text-sm font-semibold text-gray-400">접수된 신청이 없습니다</p>
        <p className="text-xs text-gray-300 mt-1">조건을 변경하거나 새로고침 해보세요</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-100">
            {['접수번호', '접수시간', '간병인', '환자', '보험사', '상태', ''].map((h) => (
              <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className={`border-b transition-colors ${
                row.is_duplicate
                  ? 'bg-amber-50/70 hover:bg-amber-50 border-amber-100'
                  : 'border-gray-50 hover:bg-gray-50/60'
              }`}
            >
              <td className="px-4 py-3">
                <span className="font-mono text-xs font-semibold text-gray-700">{row.receipt_number}</span>
              </td>
              <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                {formatDate(row.receipt_time)}
              </td>
              <td className="px-4 py-3">
                <p className="text-xs font-semibold text-gray-800 flex items-center gap-1.5">
                  {row.caregiver_name}
                  {row.is_duplicate && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[10px] font-bold whitespace-nowrap">
                      <i className="ri-error-warning-line"></i>중복
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-400">{row.caregiver_phone}</p>
              </td>
              <td className="px-4 py-3">
                <p className="text-xs font-semibold text-gray-800">{row.patient_name}</p>
                <p className="text-xs text-gray-400">{row.patient_phone}</p>
              </td>
              <td className="px-4 py-3 text-xs text-gray-500">
                {row.insurance_company ?? <span className="text-gray-300">-</span>}
              </td>
              <td className="px-4 py-3">
                <StatusDropdown
                  current={row.status}
                  onChange={async (s) => { await onStatusChange(row.id, s); }}
                />
              </td>
              <td className="px-4 py-3">
                <button
                  onClick={() => onRowClick(row)}
                  className="flex items-center gap-1 text-xs text-[#2E7D6B] hover:underline cursor-pointer whitespace-nowrap font-medium"
                >
                  상세보기 <i className="ri-arrow-right-s-line"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
